import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionSummary,
  Box,
  CircularProgress,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import Flex from '@Components/Bases/Flex';
import { useProgressBar } from 'src/Constant/ProgressBarsContext/ProgressBar.Context';
import CheckProgress from '../Components/CheckProgress';
import SidebarItem from '../Components/SidebarItem';
import {
  IMappingSidebarProgress,
  ISubItem,
  mappingResponseToSidebarProgress,
} from '../Utils';

const SidebarAccordion = styled(Accordion)(() => ({
  boxShadow: 'none',
  border: 'none',
  margin: 0,
  '&.Mui-expanded': {
    margin: 0,
  },
  '&::before': {
    height: 0,
  },
  '&.disabled': {
    pointerEvents: 'none',
    cursor: 'not-allowed',
  },
}));

const SidebarAccordionSummary = styled(AccordionSummary)(() => ({
  padding: 0,
  minHeight: 0,
  '&.Mui-expanded': {
    minHeight: 0,
  },
  '& .MuiAccordionSummary-content': {
    margin: 0,
    '&.Mui-expanded': {
      margin: '8px 0px 0px 0px',
    },
  },
  '& .MuiAccordionSummary-contentGutters': {
    margin: '8px 0px 0px 0px',
  },
}));

const SidebarMenu = () => {
  const [selectedItem, setSelectedItem] = useState<string | null>(null);

  const { progressData, progressLoading, progressError, setBreadcrumbs } =
    useProgressBar();

  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const processReport = progressData
    ? mappingResponseToSidebarProgress(progressData)
    : [];
  const startOperatingYear = progressData?.report?.startOperatingYear;
  const endOperatingYear = progressData?.report?.endOperatingYear;

  const handleSelect = (title: string, subtitle?: string, path?: string) => {
    const basePath = `/report/${id}/${path}`;
    const selectedKey = subtitle ? `${title}-${subtitle}` : title;
    setSelectedItem(selectedKey);

    if (title === 'CGR Score' && startOperatingYear) {
      setBreadcrumbs({
        title,
        startOperatingYear: startOperatingYear.toString(),
        endOperatingYear: endOperatingYear?.toString(),
        reportId: id,
      });
    } else if (subtitle && startOperatingYear) {
      setBreadcrumbs({
        title,
        startOperatingYear: startOperatingYear.toString(),
        endOperatingYear: endOperatingYear?.toString(),
        subtitle,
        reportId: id,
      });
    }

    if (title === 'ขั้นตอนการบันทึกวันที่มีมติจากคณะกรรมการบริษัท' && path) {
      if (subtitle === 'ตรวจสอบข้อมูล') {
        navigate(`${basePath}?review-data`);
        return;
      }
      if (subtitle === 'บันทึกวันที่มีมติจากคณะกรรมการบริษัท') {
        navigate(`${basePath}?record-resolution-date`);
        return;
      }
    }

    if (title === 'ขั้นตอนการเผยแพร่รายงาน' && path) {
      navigate(`${basePath}?publish-report`);
      return;
    }

    if (path) {
      navigate(basePath);
    }
  };

  useEffect(() => {
    if (!selectedItem && processReport.length) {
      const pathParts = location.pathname.split('/');
      const currentPath = pathParts.pop();
      const parentPath = pathParts[pathParts.length - 1];
      const searchParams = new URLSearchParams(location.search);
      const boardResolutionDate = searchParams.has('record-resolution-date');
      const reviewInfo = searchParams.has('review-data');
      const publishReport = searchParams.has('publish-report');

      if (currentPath) {
        const isEsgPlans = parentPath === 'esg-plans';
        const fullPath = isEsgPlans ? `esg-plans/${currentPath}` : currentPath;

        const matchedItem = processReport.find(
          (item: IMappingSidebarProgress) =>
            item.subItems?.some(
              (subItem: ISubItem) =>
                subItem.path === fullPath || subItem.path === currentPath
            )
        );

        if (publishReport) {
          handleSelect('ขั้นตอนการเผยแพร่รายงาน', 'เผยแพร่รายงาน', fullPath);
          return;
        }

        if (matchedItem) {
          const subItem = matchedItem.subItems?.find(
            (subItem: ISubItem) =>
              subItem.path === fullPath || subItem.path === currentPath
          );

          if (reviewInfo) {
            handleSelect(matchedItem.topic, 'ตรวจสอบข้อมูล', fullPath);
            return;
          }

          if (boardResolutionDate) {
            handleSelect(
              matchedItem.topic,
              'บันทึกวันที่มีมติจากคณะกรรมการบริษัท',
              fullPath
            );
            return;
          }

          if (subItem) {
            handleSelect(matchedItem.topic, subItem.name, subItem.path);
          }
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, processReport, selectedItem]);

  if (progressLoading) {
    return (
      <Flex justifyContent="center" alignItems="center" height="100%">
        <CircularProgress />
      </Flex>
    );
  }

  if (progressError) {
    return (
      <Typography color="error">
        เกิดข้อผิดพลาด ไม่สามารถโหลดข้อมูลได้
      </Typography>
    );
  }

  return (
    <>
      {processReport.map((menuItem: IMappingSidebarProgress) =>
        menuItem.subItems ? (
          <SidebarAccordion
            key={menuItem.topic}
            defaultExpanded
            className={menuItem.isDisabled ? 'disabled' : ''}
          >
            <SidebarAccordionSummary
              expandIcon={!menuItem.isDisabled && <ExpandMoreIcon />}
            >
              <Flex alignItems="center">
                <CheckProgress
                  isPass={menuItem.isPass}
                  disabled={menuItem.isDisabled}
                />
                <Typography
                  color={menuItem.isDisabled ? 'grey.600' : 'text.primary'}
                  fontWeight={600}
                  onClick={() => handleSelect(menuItem.topic)}
                  sx={{ cursor: 'pointer' }}
                >
                  {menuItem.topic}
                </Typography>
              </Flex>
            </SidebarAccordionSummary>
            {menuItem.subItems.map((subItem: ISubItem) => (
              <SidebarItem
                isDisabled={subItem.isDisabled}
                key={subItem.name}
                title={subItem.name}
                isSelected={
                  selectedItem === `${menuItem.topic}-${subItem.name}`
                }
                onSelect={() =>
                  handleSelect(menuItem.topic, subItem.name, subItem.path)
                }
                subItems={[subItem]}
              />
            ))}
          </SidebarAccordion>
        ) : (
          <Box
            key={menuItem.topic}
            onClick={() =>
              handleSelect(menuItem.topic, undefined, menuItem.path)
            }
            sx={{ cursor: 'pointer' }}
          >
            <Flex alignItems="center">
              <CheckProgress isPass={menuItem.isPass} score={menuItem.score} />
              <Typography fontWeight={800}>{menuItem.topic}</Typography>
            </Flex>
          </Box>
        )
      )}
    </>
  );
};

export default SidebarMenu;
